// src/components/DashboardCard.js
import React, { useEffect, useState } from 'react';
import { Card, CardContent, Typography, Box, Divider, CircularProgress, useTheme } from '@mui/material';
import { motion } from 'framer-motion';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  LabelList,
} from 'recharts';
import axios from '../../../utils/axiosConfig';
import AssessmentIcon from '@mui/icons-material/Assessment';
import { designTokens, commonCardStylesDark } from '../../../theme/designTokens';
import { bg, text as textColors, border, overlay } from '../../../utils/darkModeAdapter';

function DashboardCard() {
  const BACKEND_URL = process.env.REACT_APP_API_URL;
  const theme = useTheme();
  const [interviewStats, setInterviewStats] = useState({
    totalCandidates: 0,
    stageStats: {},
    decisionStats: { hired: 0, rejected: 0 }
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchInterviewStats = async () => {
      try {
        setLoading(true);
        const response = await axios.get(`${BACKEND_URL}/interview/progress-stats`, {
          withCredentials: true,
          headers: {
            'Content-Type': 'application/json'
          }
        });
        console.log('interview stats response', response.data);
        if (!response.data) {
          throw new Error('データが取得できませんでした');
        }
        setInterviewStats({
          totalCandidates: response.data.total_candidates,
          stageStats: response.data.stage_stats,
          decisionStats: response.data.decision_stats
        });
        setError(null);
      } catch (error) {
        console.error('Failed to fetch interview stats:', error);
        if (error.response?.status === 401) {
          // 401エラーはaxiosのinterceptorで処理されるため、ここでは何もしない
          console.log('401 error handled by axios interceptor');
          setError('認証エラーが発生しました。再ログインします...');
        } else {
          setError(error.response?.data?.detail || error.message || 'データの取得に失敗しました');
        }
        setInterviewStats({
          totalCandidates: 0,
          stageStats: {},
          decisionStats: { hired: 0, rejected: 0 }
        });
      } finally {
        setLoading(false);
      }
    };

    fetchInterviewStats();
  }, []);

  // 面接段階のデータを整形
  const stageData = Object.entries(interviewStats?.stageStats || {}).map(([stage, count]) => ({
    step: stage,
    count: count
  }));

  if (loading) {
    return (
      <Card
        component={motion.div}
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.3 }}
        sx={{
          backgroundColor: bg(theme).card,
          borderRadius: 3,
          border: `1px solid ${border(theme).light}`,
          boxShadow: designTokens.shadows.sm,
          overflow: 'hidden',
          position: 'relative',
        }}
      >
        <CardContent>
          <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
            <CircularProgress />
          </Box>
        </CardContent>
      </Card>
    );
  }

  if (error) {
    return (
      <Card
        component={motion.div}
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.3 }}
        sx={{
          ...commonCardStylesDark(theme),
        }}
      >
        <CardContent>
          <Box 
            display="flex" 
            flexDirection="column"
            justifyContent="center" 
            alignItems="center" 
            minHeight="400px"
            textAlign="center"
            gap={2}
          >
            <AssessmentIcon
              sx={{
                color: theme.palette.error.main,
                fontSize: '3rem'
              }}
            />
            <Typography variant="h6" color="error" sx={{ fontWeight: 600 }}>
              面接進行状況ダッシュボード
            </Typography>
            <Typography color="error.main" sx={{ maxWidth: '400px' }}>
              {error}
            </Typography>
          </Box>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card
      component={motion.div}
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.3 }}
      sx={{
        ...commonCardStylesDark(theme),
        backgroundColor: '#9C27B00D',
      }}
    >
      <CardContent sx={{ p: 3 }}>
        <Box 
          sx={{ 
            display: 'flex', 
            alignItems: 'center', 
            mb: 3,
            gap: 1
          }}
        >
          <AssessmentIcon
            sx={{
              color: '#9C27B0',
              fontSize: '2rem'
            }}
          />
          <Typography
            variant="h6"
            sx={{
              fontWeight: 600,
              color: 'text.primary',
            }}
          >
            面接進行状況ダッシュボード
          </Typography>
        </Box>

        <Divider sx={{ mb: 3 }} />

        {/* 数値の概要表示 */}
        <Box
          sx={{
            display: 'flex',
            flexWrap: 'wrap',
            gap: 2,
            mb: 4,
            justifyContent: 'space-between',
          }}
        >
          <InfoItem label="応募者総数" value={interviewStats?.totalCandidates || 0} iconColor="#9C27B0" />
          <InfoItem label="内定者数" value={interviewStats?.decisionStats?.hired || 0} iconColor={theme.palette.success.main} />
          <InfoItem label="不採用者数" value={interviewStats?.decisionStats?.rejected || 0} iconColor={theme.palette.error.main} />
        </Box>

        {/* 棒グラフ */}
        {stageData.length > 0 ? (
          <Box
            sx={{
              width: '100%',
              height: 300,
              mt: 2,
              backgroundColor: bg(theme).subtle,
              borderRadius: 2,
              p: 2,
            }}
          >
            <ResponsiveContainer>
              <BarChart data={stageData} margin={{ top: 20, right: 30, bottom: 10, left: 10 }}>
                <defs>
                  <linearGradient id="colorGradient" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#9C27B0" stopOpacity={0.9} />
                    <stop offset="100%" stopColor="#BA68C8" stopOpacity={0.7} />
                  </linearGradient>
                </defs>
                <CartesianGrid
                  strokeDasharray="3 3"
                  stroke="rgba(0,0,0,0.1)"
                  vertical={false}
                />
                <XAxis
                  dataKey="step"
                  stroke={textColors(theme).muted}
                  tick={{ fill: textColors(theme).muted, fontSize: 12 }}
                  axisLine={{ stroke: textColors(theme).disabled }}
                  tickLine={{ stroke: textColors(theme).disabled }}
                />
                <YAxis
                  stroke={textColors(theme).muted}
                  tick={{ fill: textColors(theme).muted, fontSize: 12 }}
                  axisLine={{ stroke: textColors(theme).disabled }}
                  tickLine={{ stroke: textColors(theme).disabled }}
                />
                <Tooltip
                  contentStyle={{
                    backgroundColor: bg(theme).card,
                    border: `1px solid ${border(theme).light}`,
                    borderRadius: '8px',
                    boxShadow: designTokens.shadows.md,
                    padding: '12px 16px',
                  }}
                  cursor={{
                    fill: '#9C27B015',
                    stroke: '#9C27B0',
                    strokeWidth: 1,
                  }}
                  formatter={(value) => [`${value} 名`, '応募者数']}
                  labelStyle={{
                    color: textColors(theme).primary,
                    fontWeight: 600,
                    marginBottom: '4px',
                  }}
                  itemStyle={{
                    color: '#9C27B0',
                    fontWeight: 500,
                  }}
                  animationDuration={200}
                />
                <Bar
                  dataKey="count"
                  fill="url(#colorGradient)"
                  barSize={40}
                  radius={[6, 6, 0, 0]}
                  animationDuration={800}
                  animationBegin={0}
                >
                  <LabelList
                    dataKey="count"
                    position="top"
                    style={{
                      fill: '#9C27B0',
                      fontSize: '12px',
                      fontWeight: 600,
                    }}
                    formatter={(val) => `${val}名`}
                  />
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </Box>
        ) : (
          <Box 
            sx={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              height: '300px',
              backgroundColor: overlay(theme).lightest,
              borderRadius: 2,
              border: `1px dashed ${overlay(theme).medium}`
            }}
          >
            <Typography color="text.secondary" sx={{ fontWeight: 500 }}>
              面接データがありません
            </Typography>
          </Box>
        )}
      </CardContent>
    </Card>
  );
}

export default DashboardCard;

/** 小コンポーネント: 数値表示 */
function InfoItem({ label, value, iconColor }) {
  const theme = useTheme();
  return (
    <Box
      sx={{
        flex: 1,
        minWidth: 180,
        p: 2,
        borderRadius: 2,
        backgroundColor: bg(theme).subtle,
        border: `1px solid ${border(theme).light}`,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: 1,
      }}
    >
      <Typography
        variant="body2"
        sx={{
          color: 'text.secondary',
          fontWeight: 500,
        }}
      >
        {label}
      </Typography>
      <Typography
        variant="h4"
        sx={{
          ...designTokens.typography.dataDisplayMedium,
          color: iconColor,
          lineHeight: 1
        }}
      >
        {value}
      </Typography>
    </Box>
  );
}